// Chemin: C:\gestion-copro\src\services\gardien.service.ts
// Description: Service SENTINELLE Gardien - Analyse pertinence et doublons avant archivage GED
// Session 65 - Phase 3 GED

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase';

// Initialisation Firebase Functions
const functions = getFunctions(app, 'europe-west1');

// ============================================================================
// TYPES
// ============================================================================

export interface DoublonDetecte {
  documentId: string;
  titre: string;
  similarite: 'exact' | 'probable' | 'possible';
  raison: string;
}

export interface AnalyseGardienResponse {
  verdict: 'accepter' | 'rejeter' | 'doublon' | 'verification_requise';
  confiance: number; // 0-100
  justification: string;
  doublons: DoublonDetecte[];
  suggestions: {
    archiver: boolean;
    motifRejet?: string;
    documentExistantId?: string;
  };
  metadata: {
    tempsAnalyseMs: number;
    methodeAnalyse: 'ia' | 'regles' | 'mixte';
  };
}

interface AnalyseGardienRequest {
  coproprieteId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  textContent?: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

// Formats acceptés par la GED
export const FORMATS_ACCEPTES = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx'];

// Tailles limites
export const TAILLE_MAX_MO = 25;
export const TAILLE_ALERTE_MO = 10;

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

/**
 * Vérifie si un fichier est accepté par la GED (format + taille)
 * Retourne une erreur si refusé, null si accepté
 */
export function verifierFichier(file: File): {
  accepte: boolean;
  erreur?: string;
  alerte?: string;
} {
  // Vérifier l'extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!FORMATS_ACCEPTES.includes(extension)) {
    return {
      accepte: false,
      erreur: `Format non accepté (${extension}). La GED accepte uniquement : PDF, JPG, PNG, Word (.docx) et Excel (.xlsx).`
    };
  }

  // Vérifier la taille
  const tailleMo = file.size / (1024 * 1024);
  
  if (tailleMo > TAILLE_MAX_MO) {
    return {
      accepte: false,
      erreur: `Document trop lourd (${tailleMo.toFixed(1)} Mo). Maximum autorisé : ${TAILLE_MAX_MO} Mo. Compressez-le ou conservez-le sur votre Dropbox.`
    };
  }

  if (tailleMo > TAILLE_ALERTE_MO) {
    return {
      accepte: true,
      alerte: `Document volumineux (${tailleMo.toFixed(1)} Mo). L'upload peut prendre quelques instants.`
    };
  }

  return { accepte: true };
}

/**
 * Extrait le texte d'un fichier pour l'analyse (si possible côté client)
 * Pour les PDF, on ne peut pas extraire côté client, on renvoie le nom uniquement
 */
export async function extraireTextePreview(file: File): Promise<string | undefined> {
  // Pour les fichiers texte simples, on pourrait extraire
  // Mais pour PDF/images, il faut Claude Vision côté serveur
  // On retourne undefined et la function fera l'analyse visuelle
  return undefined;
}

// ============================================================================
// SERVICE PRINCIPAL
// ============================================================================

/**
 * Appelle SENTINELLE Gardien pour analyser un fichier avant upload
 * 
 * @param file - Le fichier à analyser
 * @param coproprieteId - ID de la copropriété
 * @returns Verdict du Gardien avec justification
 */
export async function analyserAvantUpload(
  file: File,
  coproprieteId: string
): Promise<AnalyseGardienResponse> {
  // 1. Vérification locale d'abord (rapide, gratuit)
  const verification = verifierFichier(file);
  if (!verification.accepte) {
    return {
      verdict: 'rejeter',
      confiance: 100,
      justification: verification.erreur || 'Format ou taille non accepté',
      doublons: [],
      suggestions: {
        archiver: false,
        motifRejet: verification.erreur
      },
      metadata: {
        tempsAnalyseMs: 0,
        methodeAnalyse: 'regles'
      }
    };
  }

  // 2. Extraire contenu preview si possible
  const textContent = await extraireTextePreview(file);

  // 3. Appeler la Cloud Function Gardien
  const analyzeGardienFn = httpsCallable<AnalyseGardienRequest, AnalyseGardienResponse>(
    functions,
    'analyzeDocumentGardien'
  );

  try {
    const result = await analyzeGardienFn({
      coproprieteId,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      textContent
    });

    // Ajouter l'alerte taille si présente
    if (verification.alerte && result.data.verdict === 'accepter') {
      return {
        ...result.data,
        justification: `${result.data.justification} (${verification.alerte})`
      };
    }

    return result.data;

  } catch (error: unknown) {
    console.error('Erreur appel Gardien:', error);
    
    // En cas d'erreur, on propose quand même d'archiver (fallback permissif)
    return {
      verdict: 'verification_requise',
      confiance: 0,
      justification: 'Impossible d\'analyser le document. Vérification manuelle requise.',
      doublons: [],
      suggestions: {
        archiver: true
      },
      metadata: {
        tempsAnalyseMs: 0,
        methodeAnalyse: 'regles'
      }
    };
  }
}

// ============================================================================
// HELPERS POUR L'UI
// ============================================================================

/**
 * Retourne la couleur associée au verdict
 */
export function getVerdictColor(verdict: AnalyseGardienResponse['verdict']): string {
  switch (verdict) {
    case 'accepter':
      return 'green';
    case 'rejeter':
      return 'red';
    case 'doublon':
      return 'orange';
    case 'verification_requise':
      return 'amber';
    default:
      return 'gray';
  }
}

/**
 * Retourne l'icône associée au verdict (nom Lucide)
 */
export function getVerdictIcon(verdict: AnalyseGardienResponse['verdict']): string {
  switch (verdict) {
    case 'accepter':
      return 'CheckCircle';
    case 'rejeter':
      return 'XCircle';
    case 'doublon':
      return 'Copy';
    case 'verification_requise':
      return 'AlertTriangle';
    default:
      return 'HelpCircle';
  }
}

/**
 * Retourne le libellé du verdict
 */
export function getVerdictLabel(verdict: AnalyseGardienResponse['verdict']): string {
  switch (verdict) {
    case 'accepter':
      return 'Document accepté';
    case 'rejeter':
      return 'Document refusé';
    case 'doublon':
      return 'Doublon détecté';
    case 'verification_requise':
      return 'Vérification requise';
    default:
      return 'Statut inconnu';
  }
}

/**
 * Retourne le libellé de similarité pour les doublons
 */
export function getSimilariteLabel(similarite: DoublonDetecte['similarite']): string {
  switch (similarite) {
    case 'exact':
      return 'Doublon exact';
    case 'probable':
      return 'Doublon probable';
    case 'possible':
      return 'Doublon possible';
    default:
      return 'Similarité inconnue';
  }
}
